import { Avatar, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import BoxDrawer, { BoxDrawerExitMode } from 'components/BoxDrawer';
import ButtonInputClear from "components/Buttons/ButtonInputCancel";
import CardButtonFilter from "components/Buttons/CardButtonFilter";
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import MuiTextFieldColor from "components/MuiExtras/CustomInput/MuiTextFieldColor";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import { UserKnowsAboutSpecialistGrantMethod } from "models/ApiEnums";
import { TopicSpecialistDto, UserShortView } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Session from "services/Session";
import UserAutoComplete from "views/User/Components/UserAutoComplete";

/////////////////////////////////////////////////////////////////////////////////////

const MaxTakeRecords = 10;

enum FormMode { Add, Search }

interface IProps {
    specialists: TopicSpecialistDto[];
    onClose: (data: TopicSpecialistDto[]) => void;
}

interface IState {
    data: TopicSpecialistDto[];
    mode: FormMode;
    searchText: string;
    offset: number;
    dialog: any;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class TopicSpecialistsDialog extends React.PureComponent<Props, IState> {
    private searchRef: any = null;
    private addRef: any = null;

    ///
    constructor(props: Props) {
        super(props);

        this.state = {
            data: props.specialists,
            mode: FormMode.Add,
            searchText: "",
            offset: 0,
            dialog: null
        };
    }

    ///
    private handleClose = (exitMode: BoxDrawerExitMode) => {
        if (exitMode == "backdrop" || exitMode == "exit" || (this.state.mode == FormMode.Search && this.state.searchText.length == 0)) {
            this.props.onClose(this.state.data);
        }
    };

    ///
    private handleAdd(user: UserShortView) {
        if (user != null) {
            if (this.state.data.findIndex(p => p.userId == user.id) > -1)
                this.props.snackbar.showMessage(this.props.t("Views.Topic.Dialogs.TopicSpecialistsDialog.AlreadyExistsMessage"), "error");
            else {
                var newData = this.state.data.concat({
                    userId: user.id,
                    name: user.name,
                    thumbnailUrl: user.thumbnailUrl,
                    specialistGrantMethod: UserKnowsAboutSpecialistGrantMethod.TopicManager,
                    grantedBy: Session.user.id
                });

                this.setState({
                    data: newData.sort((a, b) => a.name.localeCompare(b.name)),
                    searchText: "",
                });
            }
        }
    }

    ///
    private handleExitAddMode = () => {
    };

    ///
    private handleDelete(item: TopicSpecialistDto) {
        const newData = this.state.data.filter(p => p.userId != item.userId);
        const newOffset = this.state.offset > 0 && this.state.offset > newData.length - 1
            ? this.state.offset - MaxTakeRecords
            : this.state.offset;

        this.setState({
            data: newData,
            offset: newOffset,
            dialog: null
        });
    }

    ///
    private handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchText: e.currentTarget.value });
    };

    ///
    private handleFilterClear = () => {
        this.setState(
            {
                mode: FormMode.Add,
                searchText: "",
                offset: 0
            },
            () => {
                if (this.state.mode == FormMode.Add && this.addRef) this.addRef.focus();
            });
    };

    ///
    private handleFilterSwitch = () => {
        this.setState({
            mode: this.state.mode == FormMode.Add ? FormMode.Search : FormMode.Add,
            searchText: ""
        }, () => {
            if (this.state.mode == FormMode.Search && this.searchRef) this.searchRef.focus();
            else if (this.state.mode == FormMode.Add && this.addRef) this.addRef.focus();
        });
    };


    ///
    private renderList() {
        const max = this.state.offset + MaxTakeRecords;
        const filteredData = this.state.data.filter((item, index) => item.name.toLowerCase().indexOf(this.state.searchText.toLowerCase()) > -1);
        const list = filteredData.filter((item, index) => index >= this.state.offset && index < max);

        return (
            <List dense disablePadding>

                {
                    list.map((item) => {
                        return (
                            <ListItem key={item.userId} disableGutters  >
                                <ListItemAvatar>
                                    <Avatar src={item.thumbnailUrl} />
                                </ListItemAvatar>

                                <ListItemText
                                    primary={item.name}
                                    primaryTypographyProps={{ noWrap: true, color: item.specialistGrantMethod == UserKnowsAboutSpecialistGrantMethod.TopicManager ? "secondary" : "inherit" }}
                                    secondary={
                                        item.specialistGrantMethod == UserKnowsAboutSpecialistGrantMethod.TopicRules
                                            ? this.props.t("Views.Topic.Components.TopicWhoKnowsAbout.InputByTopicRules")
                                            : item.specialistGrantMethod == UserKnowsAboutSpecialistGrantMethod.UserManager
                                                ? this.props.t("Views.Topic.Components.TopicWhoKnowsAbout.InputByUserManager")
                                                : null
                                    }
                                    secondaryTypographyProps={{ noWrap: true }}
                                />

                                <ListItemSecondaryAction>
                                    <IconButtonDelete
                                        size="small"
                                        color="muted"
                                        onClick={() =>
                                            this.setState({
                                                dialog: <MuiConfirmation
                                                    message={this.props.t("Common.Question.ConfirmationDeleteOfMessage", { name: item.name })}
                                                    onClose={
                                                        (result) => {
                                                            if (result) this.handleDelete(item);
                                                            else this.setState({ dialog: null });
                                                        }
                                                    }
                                                />
                                            })
                                        }
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        );
                    })
                }
            </List>
        );
    }

    ///
    private renderPager() {
        const { data, offset } = this.state;

        if (data == null || data.length == 0)
            return null;

        return (
            <MuiPagination
                limit={MaxTakeRecords}
                offset={offset}
                total={data.length}
                onClick={(e, offset) => this.setState({ offset })}
            />
        );
    }

    ///
    private renderHeader() {
        return (
            <Grid container justifyContent="center" alignItems="center">
                <Grid item>
                    <CardButtonFilter
                        iconSize="large"
                        color={this.state.mode == FormMode.Add ? "inherit" : "warning"}
                        title={this.props.t("Views.Topic.Dialogs.TopicSpecialistsDialog.ButtonAddText")}
                        onClick={this.handleFilterSwitch}
                    />
                </Grid>
                <Grid item xs >
                    {
                        this.state.mode == FormMode.Search
                            ? <MuiTextFieldColor
                                fullWidth
                                autoFocus
                                placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                                inputColor="warning"
                                underlineColor="warning"
                                value={this.state.searchText}
                                onChange={this.handleFilterChange}
                                onKeyDown={
                                    (e) => {
                                        if (e.keyCode == 13) {
                                            e.preventDefault();
                                            this.setState({ offset: 0 });
                                        }
                                        else if (e.keyCode == 27) {
                                            e.preventDefault();
                                            this.handleFilterClear();
                                        }
                                    }
                                }
                                endAdornment={
                                    this.state.searchText == ""
                                        ? null
                                        : <ButtonInputClear color="warning" onClick={this.handleFilterClear} />
                                }
                                inputRef={(ref) => this.searchRef = ref}
                            />
                            : <UserAutoComplete
                                autoFocus
                                escapeOnEmpty
                                overModal
                                selectedItems={null}
                                placeholder={this.props.t("Views.Topic.Dialogs.TopicSpecialistsDialog.UserPlaceholder")}
                                inputColor={"inherit"}
                                underlineColor={"inherit"}
                                onChange={(data) => this.handleAdd(data[0])}
                                onEscape={this.handleExitAddMode}
                                onBlur={this.handleExitAddMode}
                                inputRef={(ref) => this.addRef = ref}
                            />
                    }
                </Grid>
            </Grid>
        );
    }

    ///
    ///
    public render() {


        return (
            <BoxDrawer
                open
                overflow="hidden"
                onClose={this.handleClose}
                title={this.props.t("Views.Topic.Dialogs.TopicSpecialistsDialog.BoxTitle")}
            >
                <MuiPaper dense fullHeight elevation={0}>

                    <GridEx container wrap="nowrap" direction="column" fullHeight>

                        <GridEx item >
                            <MuiContainer>
                                {this.state.dialog}
                                {this.renderHeader()}
                            </MuiContainer>
                        </GridEx>

                        <GridEx item xs overflow="auto">
                            {
                                this.state.data.length == 0
                                    ? <Typography variant="subtitle2">{this.props.t("Views.Topic.Dialogs.TopicSpecialistsDialog.EmptyData")}</Typography>
                                    : this.renderList()
                            }
                        </GridEx>

                        <GridEx item>
                            {this.renderPager()}
                        </GridEx>

                    </GridEx>

                </MuiPaper>
            </BoxDrawer>
        );
    }
}

export default withSnackbar(withTranslation()(TopicSpecialistsDialog));